import React, { useState, useEffect } from "react";
import { Phone, Mail } from "lucide-react";
import Logo from "../../components/logo";
import LanguageSwitcher from "../../components/languageSwitcher";
import { ReactComponent as TwitterIcon } from "../../assets/twitter.svg";
import { ReactComponent as InstagramIcon } from "../../assets/instagram.svg";
import { ReactComponent as FacebookIcon } from "../../assets/facebook.svg";
import { ReactComponent as LinkedinIcon } from "../../assets/linkedin.svg";
import { Link } from "react-router-dom";
import ThemeToggle from "../../components/themeToggle";
import { useAppContext } from "../../context";

const tabs = [
  { id: "social", label: "Social Crowdfunding" },
  { id: "ikimina", label: "IKIMINA" },
  { id: "usave", label: "U-Save" },
  // { id: "uinvest", label: "U-Invest" },
];

function App() {
  const [activeTab, setActiveTab] = useState("social");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const { isDarkMode } = useAppContext();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeTab]);

  // Update the active tab based on the URL hash on page load
  useEffect(() => {
    const hash = window.location.hash.replace("#", "");
    if (tabs.some((tab) => tab.id === hash)) {
      setActiveTab(hash);
    }
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setIsSidebarVisible(false);
      } else {
        setIsSidebarVisible(true);
      }
    };

    handleResize(); // Set initial state
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Update the URL hash when the active tab changes
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    window.location.hash = tabId;
    setIsMenuOpen(false); // Close the menu on tab change
  };

  const renderContent = () => {
    switch (activeTab) {
      case "ikimina":
        return (
          <>
            <h2 className="text-xl font-semibold  mb-4">
              Privacy Policy for Group Savings (Ikimina)
            </h2>

            <div className="text-sm  mb-8">
              <span className="font-medium">Effective Date:</span> 03-04-2025
            </div>

            <div className="space-y-8">
              <p className="">
                At Uplus, we value your privacy and are committed to protecting
                your personal data. This Privacy Policy explains how we collect,
                use, and share information related to our Group Savings
                (Ikimina) product.
              </p>

              <section>
                <h3 className="text-lg font-medium  mb-3">
                  1. Information We Collect
                </h3>
                <p className=" mb-2">
                  We may collect the following information from users:
                </p>
                <ul className="space-y-2  list-disc pl-5">
                  <li>
                    Personal information (e.g., name, phone number, email
                    address, national ID)
                  </li>
                  <li>
                    Financial data (e.g., contributions, withdrawals, group
                    balances, and transaction history)
                  </li>
                  <li>
                    Group details (e.g., group name, members, contribution
                    rules)
                  </li>
                </ul>
              </section>

              <section>
                <h3 className="text-lg font-medium  mb-3">
                  2. How We Use Your Information
                </h3>
                <p className=" mb-2">We use your data to:</p>
                <ul className="space-y-2  list-disc pl-5">
                  <li>Facilitate and manage group savings activities</li>
                  <li>Process transactions securely</li>
                  <li>Verify user identity and prevent fraud</li>
                  <li>Improve our services and customer support</li>
                  <li>Comply with legal and regulatory requirements</li>
                </ul>
              </section>

              <section>
                <h3 className="text-lg font-medium  mb-3">
                  3. Data Sharing and Security
                </h3>
                <ul className="space-y-2  list-disc pl-5">
                  <li>
                    We do not sell or rent your personal data to third parties.
                  </li>
                  <li>
                    We may share data with regulatory authorities when required
                    by law.
                  </li>
                  <li>
                    We implement security measures to protect your data from
                    unauthorised access.
                  </li>
                </ul>
              </section>

              <section>
                <h3 className="text-lg font-medium  mb-3">4. Your Rights</h3>
                <p className=" mb-2">You have the right to:</p>
                <ul className="space-y-2  list-disc pl-5">
                  <li>Access, update, or delete your personal information</li>
                  <li>Opt out of marketing communications</li>
                  <li>Request clarification about data usage</li>
                </ul>
              </section>

              <section>
                <h3 className="text-lg font-medium  mb-3">
                  For support, contact
                </h3>
                <div className="space-y-4 ">
                  <p className="font-medium">Uplus Mutual Partners Ltd</p>
                  <p>Kigali, Rwanda</p>
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    <a
                      href="tel:+250794776745"
                      className="text-blue-600 underline"
                    >
                      +250 794776745
                    </a>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    <a
                      href="mailto:support@uplus.com"
                      className="text-blue-600 underline"
                    >
                      support@uplus.rw
                    </a>
                  </div>
                </div>
              </section>
            </div>
          </>
        );
      case "usave":
        return (
          <>
            <h1 className="text-xl font-semibold  mb-4">
              U-SAVE PRIVACY POLICY
            </h1>
            <p className="text-sm  mb-6">Effective Date: 03-04-2025</p>

            <div className="space-y-6">
              {/* Section 1 */}
              <section>
                <h2 className="text-lg font-semibold mb-2">1. Introduction</h2>
                <p className="text-sm">
                  This Privacy Policy explains how Uplus collects, uses, and
                  protects your personal information when using U-Save. By using
                  U-Save, you agree to the terms outlined in this policy.
                </p>
              </section>

              {/* Section 2 */}
              <section>
                <h2 className="text-lg font-semibold mb-2">
                  2. Information We Collect
                </h2>
                <ul className="list-disc list-inside text-sm">
                  <li>
                    Personal information such as full name, phone number, email
                    address, and national ID/passport number.
                  </li>
                  <li>
                    Transaction data related to deposits, withdrawals, and
                    account activity.
                  </li>
                  <li>
                    Device and login information to enhance security and prevent
                    unauthorized access.
                  </li>
                  <li>
                    Communications with Uplus customer support for service
                    improvements.
                  </li>
                </ul>
              </section>

              {/* Section 3 */}
              <section>
                <h2 className="text-lg font-semibold mb-2">
                  3. How We Use Your Information
                </h2>
                <ul className="list-disc list-inside text-sm">
                  <li>To provide, maintain, and improve U-Save services.</li>
                  <li>
                    To verify identities, authenticate users, and prevent fraud.
                  </li>
                  <li>To comply with legal and regulatory requirements.</li>
                  <li>
                    To notify users about changes in service terms, fees, or
                    security updates.
                  </li>
                  <li>
                    To analyze transaction trends for service optimization.
                  </li>
                </ul>
              </section>

              {/* Section 4 */}
              <section>
                <h2 className="text-lg font-semibold mb-2">
                  4. Data Protection and Security
                </h2>
                <ul className="list-disc list-inside text-sm">
                  <li>
                    Uplus implements encryption and other security measures to
                    protect user data from unauthorized access.
                  </li>
                  <li>
                    Users are responsible for securing their login credentials
                    and account details.
                  </li>
                  <li>
                    In case of a data breach, affected users will be notified
                    promptly as required by law.
                  </li>
                </ul>
              </section>

              {/* Section 5 */}
              <section>
                <h2 className="text-lg font-semibold mb-2">
                  5. Sharing of Information
                </h2>
                <ul className="list-disc list-inside text-sm">
                  <li>
                    Uplus does not sell or share personal data with third
                    parties for marketing purposes.
                  </li>
                  <li>
                    Information may be shared with regulators, law enforcement
                    agencies, or legal entities as required by law.
                  </li>
                  <li>
                    Service providers contracted by Uplus may have access to
                    data strictly for operational purposes.
                  </li>
                  <li>
                    Violation of terms may result in account suspension or
                    termination.
                  </li>
                </ul>
              </section>

              {/* Section 6 */}
              <section>
                <h2 className="text-lg font-semibold mb-2">6. User Rights</h2>
                <ul className="list-disc list-inside text-sm">
                  <li>
                    Users may request access to their stored personal data.
                  </li>
                  <li>
                    Users can request corrections or deletion of inaccurate or
                    outdated information, subject to legal limitations.
                  </li>
                  <li>
                    Users can opt out of certain communications, except those
                    required for service notifications and security alerts.
                  </li>
                </ul>
              </section>

              {/* Section 7 */}
              <section>
                <h2 className="text-lg font-semibold mb-2">
                  7. Data Retention
                </h2>
                <p className="text-sm">
                  Uplus retains user data for as long as required to comply with
                  legal obligations, resolve disputes, and enforce agreements.
                </p>
              </section>

              {/* Section 8 */}
              <section>
                <h2 className="text-lg font-semibold mb-2">
                  8. Changes to the Privacy Policy
                </h2>
                <p className="text-sm">
                  Uplus may update this Privacy Policy periodically. Any changes
                  will be communicated through our website or via email.
                </p>
              </section>

              {/* Section 9 */}
              <section>
                <h2 className="text-lg font-semibold mb-2">
                  9. Contact Information
                </h2>
                <p className="text-sm">
                  For any questions or concerns regarding this Privacy Policy,
                  please contact us at{" "}
                  <a href="mailto:support@uplus.rw" className="text-blue-600">
                    support@uplus.rw
                  </a>
                  .
                </p>
              </section>
            </div>
          </>
        );

      case "uinvest":
        return (
          <>
            <h1 className="text-xl font-semibold  mb-4">
              PRIVACY POLICY FOR U-INVEST BY UPLUS MUTUAL PARTNERS
            </h1>
            <p className="text-sm text-gray-500 mb-6">
              Effective Date: 03-02-2023
            </p>

            <div className="space-y-6">
              {/* INTRODUCTION */}
              <section>
                <h2 className="text-xl font-semibold mb-2">INTRODUCTION</h2>
                <p className="text-sm mb-2">
                  These Terms and Conditions shall apply to the use of the
                  U-Invest service (hereinafter referred to as &ldquo;the
                  Service&rdquo;) provided by Uplus Mutual Partners (hereinafter
                  referred to as &ldquo;Uplus&rdquo;). You are requested to read
                  and understand these Terms and Conditions before subscribing
                  to the Service.
                </p>
                <p className="text-sm mb-2">By accepting the terms, you:</p>
                <ul className="list-disc list-inside text-sm mb-2">
                  <li>Acknowledge that you have read them.</li>
                  <li>Understand and agree to be bound by these terms.</li>
                  <li>
                    Agree to be contacted by Uplus or its affiliates for various
                    campaigns and promotional activities.
                  </li>
                  <li>
                    Consent to being contacted for more information regarding
                    your use of the Service.
                  </li>
                  <li>
                    Acknowledge that U-Invest funds are managed by BK Capital,
                    and Uplus does not manage the funds but acts as a
                    facilitator of the investment process.
                  </li>
                </ul>
                <p className="text-sm">
                  For any clarification regarding these terms, please contact
                  Uplus directly through its offices or via the contact details
                  on its website. These Terms and Conditions shall prevail over
                  any other communication.
                </p>
              </section>

              {/* Fund Management */}
              <section>
                <h2 className="text-xl font-semibold mb-2">Fund Management</h2>
                <p className="text-sm">
                  U-Invest funds are managed by BK Capital. Uplus does not
                  manage the funds but acts as a facilitator of the investment
                  process. All fund-related decisions, including investment
                  allocations and returns, are handled by BK Capital.
                </p>
              </section>

              {/* Security Responsibilities */}
              <section>
                <h2 className="text-xl font-semibold mb-2">
                  SECURITY RESPONSIBILITIES
                </h2>
                <p className="text-sm mb-2">
                  You acknowledge that the Service is a target for fraudulent
                  schemes and should take necessary precautions to safeguard it
                  against misuse, including, but not limited to:
                </p>
                <ul className="list-disc list-inside text-sm mb-2">
                  <li>
                    <strong>Phishing:</strong> Fraudulent communications
                    claiming to be from Uplus or other reputable sources may
                    attempt to steal your security details. Uplus will never
                    request your security details.
                  </li>
                  <li>
                    <strong>Spyware:</strong> Malicious software can be
                    unknowingly installed on your device to capture sensitive
                    information. Ensure you have up-to-date anti-virus and
                    anti-spyware software.
                  </li>
                  <li>
                    Choose a strong password when registering, avoid using
                    personal information, and change it regularly.
                  </li>
                  <li>
                    Keep your password confidential and do not disclose it to
                    anyone, including Uplus staff.
                  </li>
                  <li>
                    Notify Uplus immediately if you suspect your password has
                    been compromised.
                  </li>
                </ul>
                <p className="text-sm">
                  Failure to comply with these measures may result in
                  unauthorized access, for which you will be held responsible.
                </p>
              </section>

              {/* Compliance */}
              <section>
                <h2 className="text-xl font-semibold mb-2">
                  COMPLIANCE WITH POLICIES, LAWS, AND REGULATIONS
                </h2>
                <p className="text-sm mb-2">
                  Uplus reserves the right to take any necessary action to
                  comply with applicable laws, policies, and regulations,
                  including:
                </p>
                <ul className="list-disc list-inside text-sm">
                  <li>Investigating transactions for compliance purposes.</li>
                  <li>
                    Reporting any suspicious activity to regulatory authorities.
                  </li>
                  <li>
                    Requesting additional documentation from you to comply with
                    anti-money laundering regulations.
                  </li>
                </ul>
              </section>

              {/* User Instructions */}
              <section>
                <h2 className="text-xl font-semibold mb-2">
                  USER INSTRUCTIONS
                </h2>
                <p className="text-sm mb-2">
                  By using the Service, you authorize Uplus to accept, follow,
                  and act upon instructions sent using your password. Uplus
                  shall not be liable for any loss resulting from acting on such
                  instructions unless you have reported unauthorized access
                  promptly.
                </p>
                <ul className="list-disc list-inside text-sm">
                  <li>
                    Instructions using your password are considered irrevocable
                    and binding.
                  </li>
                  <li>
                    Uplus may verify transactions via telephone or other means
                    before processing them.
                  </li>
                  <li>
                    You have the right to modify, suspend, or withdraw access to
                    the Service, subject to reasonable notice.
                  </li>
                  <li>
                    You remain responsible for all transactions conducted
                    through the Service.
                  </li>
                </ul>
              </section>

              {/* Availability and Modifications */}
              <section>
                <h2 className="text-xl font-semibold mb-2">
                  AVAILABILITY AND MODIFICATIONS
                </h2>
                <p className="text-sm">
                  Uplus will take reasonable steps to ensure the Service remains
                  available. However, maintenance, demand, or unforeseen
                  circumstances may affect availability. In such cases, Uplus
                  may modify or discontinue the Service with reasonable notice.
                </p>
              </section>

              {/* Intellectual Property */}
              <section>
                <h2 className="text-xl font-semibold mb-2">
                  INTELLECTUAL PROPERTY
                </h2>
                <p className="text-sm">
                  All intellectual property related to the Service—including
                  trademarks, copyrights, and patents—is owned by Uplus. You are
                  granted a non-exclusive license to use the Service solely for
                  its intended purposes. Unauthorized modifications, reverse
                  engineering, or reproduction is strictly prohibited.
                </p>
              </section>

              {/* Liability */}
              <section>
                <h2 className="text-xl font-semibold mb-2">LIABILITY</h2>
                <p className="text-sm mb-2">
                  You agree to indemnify and hold Uplus harmless from any
                  claims, losses, damages, or expenses arising from:
                </p>
                <ul className="list-disc list-inside text-sm">
                  <li>Unauthorized use of the Service.</li>
                  <li>
                    Internet access disruptions affecting your use of the
                    Service.
                  </li>
                  <li>
                    Technical failures or security breaches beyond Uplus'
                    control.
                  </li>
                </ul>
              </section>

              {/* Anti-Money Laundering */}
              <section>
                <h2 className="text-xl font-semibold mb-2">
                  ANTI-MONEY LAUNDERING AND LEGAL COMPLIANCE
                </h2>
                <p className="text-sm mb-2">
                  You agree that transactions through the Service will not
                  violate any local or international laws. Uplus reserves the
                  right to:
                </p>
                <ul className="list-disc list-inside text-sm">
                  <li>
                    Refuse or delay transactions suspected of illegal activity.
                  </li>
                  <li>
                    Report any suspicious activities to relevant authorities.
                  </li>
                </ul>
              </section>
            </div>
          </>
        );
      default:
        return (
          <>
            <h2 className="text-xl font-semibold  mb-4">
              Privacy Policy for Uplus Social Crowdfunding Product
            </h2>

            <div className="text-sm  mb-8">
              <span className="font-medium">Effective Date:</span> 03-04-2025
            </div>

            <div className="space-y-8">
              <section>
                <h3 className="text-lg font-medium  mb-3">1. Introduction</h3>
                <p className="">
                  Uplus is committed to protecting your privacy. This Privacy
                  Policy explains how we collect, use, disclose, and safeguard
                  your information when you use our social crowdfunding
                  platform.
                </p>
              </section>

              <section>
                <h3 className="text-lg font-medium  mb-3">
                  2. Information We Collect
                </h3>
                <ul className="space-y-3 ">
                  <li>
                    <span className="font-medium">Personal Information:</span>{" "}
                    Name, email, phone number, and payment details.
                  </li>
                  <li>
                    <span className="font-medium">Transaction Data:</span>{" "}
                    Contributions made, received, and withdrawal details.
                  </li>
                  <li>
                    <span className="font-medium">Usage Data:</span> IP
                    addresses, device information, and interactions with our
                    platform.
                  </li>
                </ul>
              </section>

              <section>
                <h3 className="text-lg font-medium  mb-3">
                  3. How We Use Your Information
                </h3>
                <ul className="space-y-2 ">
                  <li>
                    • Facilitate crowdfunding campaigns and financial
                    transactions.
                  </li>
                  <li>
                    • Communicate with users regarding their accounts and
                    platform updates.
                  </li>
                  <li>• Improve security and prevent fraudulent activities.</li>
                  <li>• Comply with legal obligations.</li>
                </ul>
              </section>

              <section>
                <h3 className="text-lg font-medium  mb-3">
                  8. Contact Information
                </h3>
                <div className="space-y-4 ">
                  <p className="font-medium">Uplus Mutual Partners Ltd</p>
                  <p>Kigali, Rwanda</p>
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    <a
                      href="tel:+250794776745"
                      className="text-blue-600 underline"
                    >
                      +250 794776745
                    </a>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    <a
                      href="mailto:support@uplus.com"
                      className="text-blue-600 underline"
                    >
                      support@uplus.rw
                    </a>
                  </div>
                </div>
              </section>
            </div>
          </>
        );
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-defaultBlack dark:text-white text-defaultBlack">
      {/* Fixed Header */}
      <nav className="fixed top-0 left-0 right-0 px-6 py-4 flex items-center justify-between bg-teal-900 backdrop-blur-sm z-50">
        <div className="flex items-center">
          <Logo />
        </div>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center space-x-4">
          <LanguageSwitcher />
          <ThemeToggle isNewDesign={true} />
        </div>

        {/* Mobile Menu Toggle */}
        <div className="md:hidden flex items-center">
          <button
            className="text-white focus:outline-none"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M4 6h16M4 12h16m-7 6h7"
              ></path>
            </svg>
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="absolute top-16 left-0 w-full bg-teal-900 text-white flex flex-col items-start p-4 space-y-4 z-50">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`block text-left px-4 py-2 w-full ${
                  activeTab === tab.id ? "bg-teal-700 font-medium" : ""
                }`}
              >
                {tab.label}
              </button>
            ))}
            <div className="flex flex-col items-start space-y-4 mt-4">
              <LanguageSwitcher />
              <div className="flex items-center space-x-2">
                <ThemeToggle isNewDesign={true} />
                <span className="font-medium">
                  {isDarkMode ? "Dark" : "Light"} Mode
                </span>
              </div>
            </div>
          </div>
        )}
      </nav>
      <div className="fixed top-16 left-0 right-0 shadow z-10  bg-white dark:bg-defaultBlack">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="py-6 text-xl font-bold ">PRIVACY POLICY</h1>
        </div>
      </div>

      {/* Main Content - Adjusted for fixed header */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 pt-40 pb-8">
        <div className="flex flex-col md:flex-row gap-8">
          {/* Sidebar Navigation - Fixed */}
          {isSidebarVisible && (
            <div className="w-full md:w-fit flex-shrink-0">
              <nav className="relative border-l border-gray-200 dark:border-gray-500/50 md:fixed flex flex-col">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => handleTabChange(tab.id)}
                    className={`block text-left px-4 py-3 relative hover:text-defaultGreen transition-colors
                      ${
                        activeTab === tab.id
                          ? "text-defaultGreen font-medium"
                          : " "
                      }
                      ${
                        activeTab === tab.id
                          ? "before:absolute before:left-0 before:top-0 before:bottom-0 before:w-0.5 before:bg-defaultGreen"
                          : ""
                      }
                    `}
                  >
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>
          )}

          {/* Content Area - Scrollable */}
          <div className="flex-1  rounded-lg shadow p-6 lg:p-8 md:ml-[200px]">
            <div className="max-w-4xl ">{renderContent()}</div>
          </div>
        </div>
      </div>
      <footer className="bg-[rgb(0,73,69)] text-white py-10 bg-radial-gradient-2 z-[100] relative">
        <div className="container mx-auto px-4 flex flex-col md:flex-row justify-between items-center space-y-8 md:space-y-0">
          {/* Logo & Description */}
          <div className="mb-8 md:mb-0">
            <Logo />
            <p className="text-sm max-w-xs mt-4">
              A digital group financing platform which enables people to pool
              funds together in a group.
            </p>
            {/* Social Icons */}
            <div className="flex space-x-6 mt-6">
              <a
                href="https://linkedin.com/company/uplus-mutual-partners/"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-defaultYellow duration-150"
              >
                <LinkedinIcon className="w-7 h-7" />
              </a>
              <a
                href="https://facebook.com/UplusRw"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-defaultYellow duration-150"
              >
                <FacebookIcon className="w-7 h-7" />
              </a>
              <a
                href="https://twitter.com/Uplusrw"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-defaultYellow duration-150"
              >
                <TwitterIcon className="w-7 h-7" />
              </a>
              <a
                href="https://instagram.com/Uplusrw/"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-defaultYellow duration-150"
              >
                <InstagramIcon className="w-7 h-7" />
              </a>
            </div>
          </div>

          {/* Links Sections */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
            <div>
              <h4 className="font-bold mb-3">Support</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/#" className="hover:text-yellow-500">
                    Home
                  </Link>
                </li>
                <li>
                  <Link to="/#benefits" className="hover:text-yellow-500">
                    Benefits
                  </Link>
                </li>
                <li>
                  <Link to="/#products" className="hover:text-yellow-500">
                    Products
                  </Link>
                </li>
                <li>
                  <Link to="/#faqs" className="hover:text-yellow-500">
                    FAQs
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-3">Help</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/help-center" className="hover:text-yellow-500">
                    Help Center
                  </Link>
                </li>
                <li>
                  <Link
                    to="/terms-conditions"
                    className="hover:text-yellow-500"
                  >
                    Terms & Conditions
                  </Link>
                </li>
                <li>
                  <Link to="/privacy-policy" className="hover:text-yellow-500">
                    Privacy Policy
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-3">Product</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/#products" className="hover:text-yellow-500">
                    Social CrowdFund
                  </Link>
                </li>
                <li>
                  <Link to="/#products" className="hover:text-yellow-500">
                    Ikimina
                  </Link>
                </li>
                <li>
                  <Link to="/#products" className="hover:text-yellow-500">
                    U-Save
                  </Link>
                </li>
                <li>
                  <Link to="/#products" className="hover:text-yellow-500">
                    U-Invest
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          {/* Download Buttons - Aligned Vertically */}
          <div className="hidden lg:block">
            <div className="flex flex-col space-y-4">
              <a
                href="https://apple.co/2KnmQCE"
                className="bg-white dark:bg-black dark:text-white px-3 py-3 rounded-lg flex items-center space-x-2 text-defaultBlack w-fit"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src="https://upload.wikimedia.org/wikipedia/commons/thumb/6/67/App_Store_%28iOS%29.svg/2048px-App_Store_%28iOS%29.svg.png"
                  alt="App Store"
                  className="w-6 h-6"
                />
                <span>App Store</span>
              </a>
              <a
                href="http://bit.ly/uplusRW"
                className="bg-white dark:bg-black dark:text-white px-3 py-3 rounded-lg flex items-center space-x-2 text-defaultBlack w-fit"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src="https://upload.wikimedia.org/wikipedia/commons/thumb/d/d0/Google_Play_Arrow_logo.svg/1024px-Google_Play_Arrow_logo.svg.png"
                  alt="Play Store"
                  className="w-6 h-6"
                />
                <span>Play Store</span>
              </a>
            </div>
          </div>
        </div>
        {/* Horizontal Divider */}
        <div className="w-3/4 mx-auto border-t border-white/50 mt-10"></div>

        {/* Footer Note */}
        <div className="text-center mt-6">
          <p className="text-sm">Uplus Mutual Partners Ltd. ©2025</p>
        </div>
      </footer>
    </div>
  );
}

export default App;
